/**
 * useGatedAction — wraps a click handler so unauthorized users hit the
 * upsell modal instead of executing the action.
 *
 * Usage:
 *   const pdf = useGatedAction("pdf_reports");
 *   <Button onClick={() => pdf.run(downloadPDF)} disabled={pdf.locked} ... />
 *
 * The hook itself doesn't render anything; it just decides whether to
 * forward the call to `fn` or emit an upsell event. Pair it with
 * <LockedBadge/> for the visual cue.
 *
 * Why a hook instead of a wrapped <Button>: many actions live inside
 * complex forms / dropdowns where wrapping the button would force us
 * to fork the parent component. The hook lets us guard inline at the
 * click handler without restructuring.
 */
import { useEntitlements } from "./useEntitlements";
import { emitUpsellEvent } from "../contexts/UpsellEvents";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

export function useGatedAction(featureKey) {
  const { has, isLoading } = useEntitlements();
  const locked = !isLoading && !has(featureKey);

  // Lazy-resolve addon hint only when the user actually lacks the
  // feature — entitled users pay no cost.
  const { data: addonHint } = useQuery({
    queryKey: ["addon-for-feature", featureKey],
    queryFn: async () => (await api.get(`/billing/addon-for-feature/${featureKey}`)).data,
    enabled: locked,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const run = (fn, ...args) => {
    if (locked) {
      emitUpsellEvent({
        feature_key: featureKey,
        addon_plan_id: addonHint?.plan_id || null,
        source_url: window.location.pathname,
      });
      return;
    }
    if (typeof fn === "function") fn(...args);
  };

  return { run, locked, isLoading };
}
